import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { FormGroup } from 'src/components/ui';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import Icon from 'src/components/Icon';
import { sanitizeQuillHtml } from 'src/util/sanitizeQuillHtml';

// HTML is "complex" (externally generated) when it carries inline styles or tables —
// both of which Quill's delta model will strip or mangle on load.
function isComplexHtml(value) {
  if (!value) return false;
  return /<table[\s>]/i.test(value) || /style\s*=/i.test(value);
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image', 'video'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

const QUILL_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'width',
  'height',
  'style',
  'src',
  'allowfullscreen',
  'iframe',
];

export default function HtmlEditor({
  id,
  schema: { title, description },
  uiSchema,
  formData: value,
  onChange: onChangeForm,
  fieldPathId: { path },
  flushRef,
}) {
  const [current, setCurrent] = useState(value);
  // Start in preview when content already exists, edit when the field is empty.
  const [mode, setMode] = useState('preview');

  const [loading, setLoading] = useState(true);

  const isClean = useRef(true);
  useDebouncedEffect(
    () => {
      if (current !== value) {
        onChangeForm(current, path);
        isClean.current = false;
      }
    },
    [current],
    450
  );

  // Lets an owner commit text still waiting on the debounce (before saving, previewing or switching pages).
  // Read through a ref so the flush always sees the latest text, value and handler.
  const latestRef = useRef(null);
  latestRef.current = { current, value, onChangeForm, path };
  const flush = useCallback(() => {
    const latest = latestRef.current;
    if (latest && latest.current !== latest.value) {
      latest.onChangeForm(latest.current, latest.path);
      isClean.current = false;
    }
  }, []);
  if (flushRef) flushRef.current = flush;

  // Unmounting cancels the pending debounce; owners that opted in get the text instead of losing it.
  useEffect(() => (flushRef ? flush : undefined), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setCurrent(value), [value]);

  // External CSS (RJSF grid, bootstrap-compat, Quill) changes .cm-gutter's flex-direction
  // to row, laying line-numbers horizontally and hiding the content area. Injecting via a
  // <style> tag into <head> wins the specificity battle that EditorView.theme() loses.
  useEffect(() => {
    const id = 'cm-layout-fix';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = [
      '.cm-editor .cm-scroller { display: flex !important; flex-direction: row !important; }',
      '.cm-editor .cm-gutters { display: flex !important; flex-direction: row !important; flex: 0 0 auto !important; }',
      '.cm-editor .cm-gutter { display: flex !important; flex-direction: column !important; }',
      '.cm-editor .cm-content { flex: 1 1 auto !important; min-width: 0 !important; }',
    ].join('\n');
    document.head.appendChild(el);
  }, []);

  const complex = useMemo(() => isComplexHtml(current), [current]);
  const sanitizedHtml = useMemo(() => ({ __html: DOMPurify.sanitize(current || '') }), [current]);
  const quillValue = useMemo(() => sanitizeQuillHtml(current), [current]);
  const htmlExtensions = useMemo(() => [html()], []);

  return (
    <FormGroup>
      {title && (
        <label className="control-label" htmlFor={id}>
          {title}
        </label>
      )}
      {description && <div className="field-description">{description}</div>}

      {/* Tab bar — Preview | Edit (Raw HTML is accessed via the confirm gate, not a top-level tab) */}
      <div className="flex gap-1 mb-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setMode('preview')}
          className={`flex flex-row px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mode === 'preview' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icon icon="Eye" className="mr-1.5" />
          <Trans>Preview</Trans>
        </button>
        <button
          type="button"
          onClick={() => (complex && mode === 'preview' ? setMode('confirm') : setMode('edit'))}
          className={`flex flex-row px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mode === 'edit' || mode === 'raw' || mode === 'confirm'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icon icon="Pencil" className="mr-1.5" />
          <Trans>Edit</Trans>
        </button>
      </div>

      {/* ── Preview ── */}
      {mode === 'preview' && (
        <>
          <div className="border border-gray-200 rounded p-4 min-h-[200px] overflow-auto bg-white" dangerouslySetInnerHTML={sanitizedHtml} />
          <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
            <Icon icon="Pencil" />
            <Trans>
              Switch to the{' '}
              <button type="button" className="underline hover:text-gray-600" onClick={() => (complex ? setMode('confirm') : setMode('edit'))}>
                Edit tab
              </button>{' '}
              to modify this content.
            </Trans>
          </p>
        </>
      )}

      {/* ── Confirm gate — shown instead of Quill when content is complex ── */}
      {mode === 'confirm' && (
        <div className="border border-amber-200 rounded bg-amber-50 p-4 flex flex-col gap-3">
          <div className="flex items-start gap-2 text-amber-800">
            <Icon icon="Warning" className="mt-0.5 shrink-0 text-amber-500" />
            <div className="text-sm">
              <p className="font-semibold mb-1">
                <Trans>This content was generated externally.</Trans>
              </p>
              <p>
                <Trans>
                  The visual editor cannot fully represent complex HTML (inline styles, tables, custom formatting). Opening it will permanently
                  simplify the formatting once you save.
                </Trans>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-primary text-white hover:opacity-90 font-medium flex items-center"
              onClick={() => setMode('raw')}
            >
              <Icon icon="Code" className="mr-1.5" />
              <Trans>Edit raw HTML (safe)</Trans>
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-100 font-medium flex items-center"
              onClick={() => setMode('edit')}
            >
              <Icon icon="Pencil" className="mr-1.5" />
              <Trans>Open in visual editor anyway</Trans>
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
              onClick={() => setMode('preview')}
            >
              <Trans>Cancel</Trans>
            </button>
          </div>
        </div>
      )}

      {/* ── Edit (Quill) — only reached after explicit confirmation for complex HTML ── */}
      {mode === 'edit' && (
        <div className="parent-scroll">
          <div id="scrolling-container">
            <ReactQuill
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              value={quillValue}
              onChange={e => setCurrent(e)}
              placeholder={t`Enter text here...`}
              id={id}
              bounds="#scrolling-container"
              scrollingContainer=".parent-scroll"
            />
          </div>
        </div>
      )}

      {/* ── Raw HTML (CodeMirror) — advanced, reached via the Edit warning ── */}
      {mode === 'raw' && (
        <div style={{ position: 'relative' }}>
          <div className="json-editor-container">
            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-600 rounded-t px-3 py-2 text-sm text-zinc-200">
              <Icon icon="Warning" className="shrink-0 text-amber-400" />
              <span className="flex-1">
                <Trans>You are editing raw HTML. Changes are applied directly to the stored value.</Trans>
              </span>
              <button type="button" className="text-zinc-400 hover:text-zinc-200 underline text-xs whitespace-nowrap" onClick={() => setMode('edit')}>
                <Trans>Back to visual editor</Trans>
              </button>
            </div>
            {/* Explicit height wrapper required: @uiw injects .cm-scroller { height: 100% !important }
                which only resolves correctly when the .cm-editor parent has a concrete px height. */}
            <div style={{ height: '300px', width: '100%', position: 'relative' }}>
              <CodeMirror
                value={current || ''}
                width="100%"
                height="100%"
                theme="dark"
                basicSetup
                extensions={htmlExtensions}
                onChange={val => setCurrent(val)}
              />
            </div>
          </div>
        </div>
      )}
    </FormGroup>
  );
}
